import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { useProducts } from '../useProducts/useProducts';
import { useAttachSuggestion } from '../useAttachSuggestion/useAttachSuggestion';
import { ScoredAlgoliaHit } from '@jbhi-fi/apis/src/algolia/ProductClient';
import { getItemsByProductCategory } from '@jbhi-fi/apis/src/utils/googleRecommended/filterGoogleRecommendations';

type PartialProduct = {
  sku: string;
  product?: { id?: string };
  category_id: number;
};

export type UseSuggestedProductsProps = {
  products?: Array<PartialProduct | undefined>;
  googleRecModel: string;
  googleRecNumProducts: number;
  googleRecAcceptedDeviationPercentage?: number;
  protectedProductCategories?: Array<number>;
  isAttachSuggestionEnabled?: boolean;
  excludedSkus?: Array<string>;
  isEnabled?: boolean;
  analyticsTags?: Array<string>;
  source?: 'pdp' | 'homepage';
  shouldFetchImsSuggestions?: boolean;
  shouldEnhanceSuggestions?: boolean;
};

export type UseSuggestedProductsResult = {
  isLoading: boolean;
  suggestions?: Array<AlgoliaProductHit>;
};

const isProductAvailable = (item: AlgoliaProductHit) =>
  item?.product_published && item?.price > 0 && item?.availability?.displayProduct && item?.availability?.canBuyOnline;

export const useSuggestedProducts = ({
  products,
  googleRecModel,
  googleRecNumProducts,
  googleRecAcceptedDeviationPercentage,
  protectedProductCategories,
  isAttachSuggestionEnabled = true,
  excludedSkus,
  isEnabled = true,
  analyticsTags,
  source,
  shouldFetchImsSuggestions = true,
  shouldEnhanceSuggestions = false,
}: UseSuggestedProductsProps): UseSuggestedProductsResult => {
  const productSkus = products?.map(p => p?.sku || p?.product?.id).filter((sku): sku is string => !!sku) || [];
  const { isLoading: isPurchasedProductLoading, products: purchasedProducts = [] } = useProducts({
    productSkus,
    options: {
      shouldResolveExtraOnlineProductFields: true,
    },
    isEnabled: isEnabled && !!productSkus?.length,
  });

  const { isLoading: isAttachCategorySuggestionsLoading } = useAttachSuggestion({
    purchasedCategoryId: products
      ?.map(p => p?.category_id)
      .filter((categoryId): categoryId is number => !!categoryId)[0],
    isAttachSuggestionEnabled,
  });

  const relatedProductSkus = purchasedProducts.flatMap(product => product.relatedProductSkus || []);

  const { isLoading: isImsRecLoading, products: _imsRecProducts } = useProducts({
    isEnabled: shouldFetchImsSuggestions && isEnabled && !!(!isPurchasedProductLoading && relatedProductSkus?.length),
    productSkus: relatedProductSkus,
    options: { shouldResolveExtraOnlineProductFields: true, shouldEnhanceSuggestions },
  });

  const { isLoading: isGoogleRecLoading, products: _googleRecProducts } = useProducts({
    isEnabled: isEnabled && !!(productSkus?.length && !isPurchasedProductLoading),
    googleRecAiModel: googleRecModel,
    googleRecNumberOfProducts: googleRecNumProducts,
    googleRecSkus: productSkus,
    options: {
      shouldResolveExtraOnlineProductFields: true,
      analyticsTags,
      shouldEnhanceSuggestions,
      acceptedDeviationPercentage: googleRecAcceptedDeviationPercentage,
    },
    source,
  });

  const recommendedProducts: Array<ScoredAlgoliaHit> = [
    ...(_imsRecProducts?.map(product => ({
      ...product,
      google_tag_manager: {
        ...product.google_tag_manager,
        dimension100: product?.category_hierarchy?.join(' / '),
        recommendationSource: 'ims',
      },
      varianceScore: Infinity,
      variance: 0,
    })) ?? []),

    ...(_googleRecProducts?.map(product => ({
      ...product,
      google_tag_manager: {
        ...product.google_tag_manager,
        dimension100: product?.category_hierarchy?.join(' / '),
        recommendationSource: 'recai',
      },
    })) ?? []),
  ].filter(isProductAvailable);

  if (!isEnabled) return { isLoading: false };
  const filteredRecommendedProducts = shouldEnhanceSuggestions
    ? getItemsByProductCategory(
        recommendedProducts,
        recommendedProducts.filter(item => excludedSkus?.some(sku => sku === item.sku)),
        protectedProductCategories
      )
    : recommendedProducts;

  return {
    isLoading: isAttachCategorySuggestionsLoading || isGoogleRecLoading || isImsRecLoading || isPurchasedProductLoading,
    suggestions: filteredRecommendedProducts,
  };
};
